import { useEffect, useState, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  useToast,
  Tag,
  TagLabel,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
  Alert,
  Tooltip,
  Box,
  Flex,
  Text,
} from '@chakra-ui/react';
import { MdAdd, MdSearch, MdDelete, MdEdit, MdVisibility, MdAccessTime, MdCheck, MdCalendarToday } from 'react-icons/md';
import { FiClock, FiList, FiCalendar, FiFlag, FiFolder, FiRefreshCw } from 'react-icons/fi';
import { Project } from '../../types/project';
import { ProjectService } from '../../services/projectService';
import { TaskService } from '../../services/taskService';
import { Task } from '../../types/task';
import { useAtom } from 'jotai';
import {
  projectsAtom,
  projectsLoadingAtom,
  projectsErrorAtom,
  fetchProjectsAtom,
  projectSearchQueryAtom,
  createProjectAtom,
  updateProjectAtom,
  deleteProjectAtom,
  searchProjectsAtom
} from '../../atoms/v0/projectAtoms';
import { tasksAtom } from '../../atoms/v0/taskAtoms';
import { backgroundRefreshingAtom } from '../../atoms/v0/uiAtoms';
import { FloatingActionButton } from '../../components/v0/common/FloatingActionButton';
import { getProjectColor } from '../../utils/colorUtils';
import { PageLayout } from '../../components/v0/PageLayout';
import { useTranslation } from 'react-i18next';
import { loadableProjectsAtom } from '../../utils/jotaiUtils';
import { translatedProjectsAtom } from '../../atoms/v0/translationAtoms';
import { motion } from 'framer-motion';
import { useAtomValue } from 'jotai';

export const ProjectList = () => {
  const { t } = useTranslation(['v0/projects', 'v0/common', 'common']);

  const [projectsLoadable] = useAtom(loadableProjectsAtom);
  const [translatedProjects] = useAtom(translatedProjectsAtom);
  const [isBackgroundRefreshing] = useAtom(backgroundRefreshingAtom);
  const [searchQuery, setSearchQuery] = useAtom(projectSearchQueryAtom);

  const [, fetchProjects] = useAtom(fetchProjectsAtom);
  const [, createProject] = useAtom(createProjectAtom);
  const [, deleteProjectAction] = useAtom(deleteProjectAtom);
  const [, searchProjects] = useAtom(searchProjectsAtom);

  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const createModalDisclosure = useDisclosure();
  const deleteConfirmDisclosure = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'white');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  const [formData, setFormData] = useState({
    title: '',
    context: '',
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();

    if (date.toDateString() === now.toDateString()) {
      return `${t('v0/common:timeGroups.today')} ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
    }

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `${t('v0/common:timeGroups.yesterday')} ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
    }

    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
    }

    return date.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  useEffect(() => {
    startTransition(() => {
      fetchProjects();
    });
  }, [fetchProjects]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchProjects();
      return;
    }

    try {
      const keywords = searchQuery.trim().split(/\s+/);
      startTransition(() => {
        searchProjects({ keywords });
      });
    } catch (error) {
      toast({
        title: t('v0/projects:errors.searchFailed'),
        description: error instanceof Error ? error.message : t('v0/common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCreateProject = async () => {
    try {
      if (!formData.title.trim()) {
        toast({
          title: t('v0/projects:errors.titleRequired'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const success = await createProject({
        title: formData.title,
        context: formData.context,
      });

      if (success) {
        toast({
          title: t('v0/projects:notifications.createSuccess'),
          description: t('v0/projects:notifications.projectCreated'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        startTransition(() => {
          setFormData({
            title: '',
            context: '',
          });
        });

        createModalDisclosure.onClose();
      }
    } catch (error) {
      toast({
        title: t('v0/projects:errors.createFailed'),
        description: error instanceof Error ? error.message : t('v0/common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteProject = async () => {
    if (!selectedProject) return;

    try {
      const success = await deleteProjectAction(selectedProject.id);

      if (success) {
        toast({
          title: t('v0/projects:notifications.deleteSuccess'),
          description: t('v0/projects:notifications.projectDeleted'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        startTransition(() => {
          setSelectedProject(null);
        });
        deleteConfirmDisclosure.onClose();
      }
    } catch (error) {
      toast({
        title: t('v0/projects:errors.deleteFailed'),
        description: error instanceof Error ? error.message : t('v0/common:errors.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/v0/projects/${projectId}`);
  };

  const ProjectCard = ({ project }: { project: Project }) => {
    const allTasks = useAtomValue(tasksAtom);
    const tasks = allTasks.filter(task => task.projectId === project.id);

    const totalTasks = tasks.length;
    const completedTasks = tasks.filter((task: Task) => task.done).length;
    const vitalTasks = tasks.filter((task: Task) => task.vital).length;
    const urgentTasks = tasks.filter((task: Task) => task.urgent).length;
    const projectColor = getProjectColor(project.id);
    const colorVar = `var(--chakra-colors-${projectColor.replace('.', '-')})`;
    const projectHexColor = getProjectColor(project.id);

    return (
      <div
        className="bg-white dark:bg-gray-800 rounded-lg relative cursor-pointer transition-all duration-150 hover:translate-y-[-2px] hover:shadow-md shadow-sm h-full"
        onClick={() => handleProjectClick(project.id)}
        style={{ }}
      >
        <div className="p-3">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center flex-1 min-w-0">
              <span className="mr-2 h-4 w-4 flex-shrink-0" style={{ color: projectHexColor }}>
                <FiFolder />
              </span>
              <h3
                className="text-base font-semibold text-gray-800 dark:text-white truncate"
                style={{ color: projectHexColor }}
              >
                {project.title}
              </h3>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap pl-2">
              {formatDate(project.createdAt)}
            </p>
          </div>

          {project.context ? (
            <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-1 mb-2">
              {project.context}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2 mt-2">
            <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-md px-2 py-1">
              <FiList size={12} className="text-gray-600 dark:text-gray-300 mr-1.5" />
              <span className="text-xs">
                <span className="font-medium">{totalTasks}</span> {t('v0/projects:card.taskCount', { count: totalTasks })}
                {completedTasks > 0 && (
                  <span className="text-gray-500 dark:text-gray-400 ml-1">
                    (<MdCheck style={{ fontSize: '10px', display: 'inline', marginRight: '1px' }} /> {completedTasks})
                  </span>
                )}
              </span>
            </div>

            {vitalTasks > 0 && (
              <div className="flex items-center bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-200 rounded-md px-2 py-1">
                <FiFlag size={12} className="mr-1.5" />
                <span className="text-xs font-medium">{vitalTasks} {t('v0/projects:card.important')}</span>
              </div>
            )}

            {urgentTasks > 0 && (
              <div className="flex items-center bg-orange-50 dark:bg-orange-900 text-orange-600 dark:text-orange-200 rounded-md px-2 py-1">
                <FiClock size={12} className="mr-1.5" />
                <span className="text-xs font-medium">{urgentTasks} {t('v0/projects:card.urgent')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const CreateProjectButton = () => (
    <Button
      leftIcon={<MdAdd />}
      colorScheme="teal"
      onClick={createModalDisclosure.onOpen}
      size="sm"
      borderRadius="full"
      fontWeight="bold"
    >
      {t('v0/projects:actions.createProject')}
    </Button>
  );

  return (
    <PageLayout title={t('v0/projects:title')} customAction={<CreateProjectButton />}>
      <div className="container mx-auto px-3 md:px-6 max-w-7xl pt-4">
        <div className="mb-4 md:mb-6">
          <InputGroup>
            <Input
              placeholder={t('v0/projects:searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              bg={cardBg}
              borderRadius="full"
              borderWidth="1px"
              _focus={{ borderColor: 'teal.500', boxShadow: '0 0 0 1px var(--chakra-colors-teal-500)' }}
            />
            <InputRightElement>
              <IconButton
                aria-label={t('v0/common:actions.search')}
                icon={<MdSearch />}
                variant="ghost"
                colorScheme="teal"
                onClick={handleSearch}
                borderRadius="full"
                size="sm"
              />
            </InputRightElement>
          </InputGroup>
        </div>

        {projectsLoadable.state === 'loading' && !isBackgroundRefreshing ? (
          <div className="flex justify-center items-center py-12">
            <div className="flex flex-col items-center gap-4">
              <Spinner size="xl" thickness="4px" color="teal.500" />
              <p className="text-gray-500">{t('v0/projects:loadingText')}</p>
            </div>
          </div>
        ) : projectsLoadable.state === 'hasError' ? (
          <div className="flex justify-center items-center py-12">
            <div className="flex flex-col items-center gap-4">
              <Box color="red.500" fontSize="3xl">{t('common:icons.warning')}</Box>
              <p className="text-red-500 mb-3">{t('v0/projects:errors.loadingFailed')}</p>
              <Button
                onClick={() => {
                  startTransition(() => {
                    fetchProjects();
                  });
                }}
                leftIcon={<FiRefreshCw />}
                colorScheme="teal"
              >
                {t('v0/common:refresh.refreshButton')}
              </Button>
            </div>
          </div>
        ) : projectsLoadable.state === 'hasData' && (translatedProjects && translatedProjects.length === 0 && projectsLoadable.data.length === 0) ? (
          <div
            className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm"
          >
            <img
              src="https://cdn-icons-png.flaticon.com/512/1055/1055645.png"
              alt={t('v0/projects:emptyState.alt')}
              className="w-[100px] h-[100px] md:w-[150px] md:h-[150px] opacity-60 mb-4"
            />
            <h2 className="text-lg font-medium text-gray-500 mb-2">{t('v0/projects:emptyState.title')}</h2>
            <p className="text-gray-400 text-center max-w-[400px] mb-6">
              {t('v0/projects:emptyState.description')}
            </p>
            <Button
              leftIcon={<MdAdd />}
              colorScheme="teal"
              onClick={createModalDisclosure.onOpen}
              size="lg"
              borderRadius="full"
            >
              {t('v0/projects:emptyState.action')}
            </Button>
          </div>
        ) : projectsLoadable.state === 'hasData' ? (
          <div
             className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity duration-200 ${isBackgroundRefreshing ? 'opacity-80' : 'opacity-100'}`}
           >
            {(translatedProjects && translatedProjects.length > 0 ? translatedProjects : projectsLoadable.data).map((project: Project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        ) : null}

        <FloatingActionButton
          onClick={createModalDisclosure.onOpen}
          colorScheme="teal"
          icon={<MdAdd />}
        />

        <Modal
          isOpen={createModalDisclosure.isOpen}
          onClose={createModalDisclosure.onClose}
          size="lg"
        >
          <ModalOverlay backdropFilter="blur(2px)" />
          <ModalContent borderRadius="xl" mx={4}>
            <ModalHeader>{t('v0/projects:createModal.title')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <div className="flex flex-col gap-4">
                <FormControl isRequired>
                  <FormLabel>{t('v0/projects:form.titleLabel')}</FormLabel>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t('v0/projects:form.titlePlaceholder')}
                    size="lg"
                    borderRadius="lg"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>{t('v0/projects:form.descriptionLabel')}</FormLabel>
                  <Textarea
                    value={formData.context}
                    onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                    placeholder={t('v0/projects:form.descriptionPlaceholder')}
                    borderRadius="lg"
                    minH="150px"
                  />
                </FormControl>

                <Button
                  mt={4}
                  colorScheme="teal"
                  size="lg"
                  width="100%"
                  borderRadius="lg"
                  onClick={handleCreateProject}
                  isLoading={projectsLoadable.state === 'loading'}
                >
                  {t('v0/projects:actions.createProject')}
                </Button>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>

        <Modal
          isOpen={deleteConfirmDisclosure.isOpen}
          onClose={deleteConfirmDisclosure.onClose}
        >
          <ModalOverlay />
          <ModalContent borderRadius="xl" mx={4}>
            <ModalHeader>{t('v0/projects:deleteModal.title')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Alert status="warning" borderRadius="md" mb={4}>
                <div>
                  {t('v0/projects:deleteModal.confirmation', { title: selectedProject?.title })}
                </div>
              </Alert>

              <div className="flex gap-3 justify-end">
                <Button onClick={deleteConfirmDisclosure.onClose}>{t('v0/common:actions.cancel')}</Button>
                <Button
                  colorScheme="red"
                  onClick={handleDeleteProject}
                  isLoading={projectsLoadable.state === 'loading'}
                >
                  {t('v0/projects:deleteModal.confirm')}
                </Button>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      </div>
    </PageLayout>
  );
};